import { UPLOADS_BASE, slugifyEstablishmentName } from '../api/hospitalApi';
import defaultHospitalBanner from '../assets/images/hospital-card-bg.jpg';
import defaultHospitalLogo from '../assets/images/hospital-profile.png';
import { filterCountries, filterCitiesForParent, isLocationUnderParent } from './locationHelpers';

export function getCountryOptionsFromLocations(locations = [], locationTypes = []) {
  const countries = locationTypes.length
    ? filterCountries(locations, locationTypes)
    : locations.filter((loc) => loc.location_type === 'Country');

  return countries.map((loc) => ({
    id: loc.location_id,
    name: loc.location_name,
  }));
}

/** City/District locations under the selected country for filter tabs. */
export function getLocationTabsForParent(locations = [], parentLocationId, locationTypes = []) {
  if (!parentLocationId) return [];

  if (locationTypes.length) {
    return filterCitiesForParent(locations, locationTypes, parentLocationId).map((loc) => ({
      id: loc.location_id,
      name: loc.location_name,
    }));
  }

  return locations
    .filter((loc) => loc.location_type === 'City/ District')
    .filter((loc) => isLocationUnderParent(locations, loc.location_id, parentLocationId))
    .map((loc) => ({
      id: loc.location_id,
      name: loc.location_name,
    }));
}

export function resolveAccreditationLabels(accreditationIds, accreditations = []) {
  if (!accreditationIds) return '';
  return accreditationIds
    .toString()
    .split(',')
    .map((id) => accreditations.find((a) => String(a.accreditation_id) === id.trim()))
    .filter(Boolean)
    .map((a) => a.accreditation_title)
    .join(', ');
}

function formatWebsiteDisplay(url) {
  if (!url) return '—';
  return url.replace(/^https?:\/\//i, '').replace(/\/$/, '');
}

function buildAddressLabel(est) {
  const parts = [
    est.address || est.address_line_1,
    est.address_line_2,
    est.city_name,
    est.state_name,
    est.country_name,
  ].filter(Boolean);
  return parts.join(', ') || '—';
}

function buildTypeLabel(est) {
  const parts = [est.category, est.establishment_type].filter(Boolean);
  return parts.length ? parts.join(' / ') : 'Healthcare';
}

function getBannerImage(est, { noDefaults = false } = {}) {
  const galleryPath = est.gallery_images?.[0]?.image_path;
  if (galleryPath) return `${UPLOADS_BASE}/uploads/${galleryPath}`;
  return noDefaults ? null : defaultHospitalBanner;
}

function getLogoImage(est, { noDefaults = false } = {}) {
  if (est.logo_path) return `${UPLOADS_BASE}/uploads/${est.logo_path}`;
  return noDefaults ? null : defaultHospitalLogo;
}

function getHoursLabel(est, { noDefaults = false } = {}) {
  if (est.emergency_dept_available || est.emergency_support_available) {
    return 'Open 24/7';
  }
  return noDefaults ? '' : 'Contact for hours';
}

export function mapEstablishmentToCard(est, { noDefaults = false } = {}) {
  const address = buildAddressLabel(est);
  const website = formatWebsiteDisplay(est.website_address);

  return {
    id: est.establishment_id,
    slug: slugifyEstablishmentName(est.establishment_name, est.establishment_id),
    banner: getBannerImage(est, { noDefaults }),
    logo: getLogoImage(est, { noDefaults }),
    name: est.establishment_name || (noDefaults ? '' : 'Hospital'),
    type: buildTypeLabel(est) === 'Healthcare' && noDefaults ? '' : buildTypeLabel(est),
    address: address === '—' && noDefaults ? '' : address,
    hours: getHoursLabel(est, { noDefaults }),
    website: website === '—' && noDefaults ? '' : website,
    cityName: est.city_name || est.location_name || '',
    countryName: est.country_name || '',
  };
}

export function mapEstablishmentToProfile(est, { accreditations = [] } = {}) {
  const accreditationDisplay = resolveAccreditationLabels(est.accreditation_ids, accreditations);

  return {
    ...est,
    establishment_id: est.establishment_id,
    slug: slugifyEstablishmentName(est.establishment_name, est.establishment_id),
    establishment_name: est.establishment_name || 'Hospital',
    accreditation_display: accreditationDisplay,
    logo_path: est.logo_path || '',
    profile_pdf_available: Boolean(est.profile_pdf_path),
  };
}

export function getUniqueCities(establishments = []) {
  const cities = establishments
    .map((est) => est.city_name || est.location_name)
    .filter(Boolean);
  return [...new Set(cities)];
}

export function getUniqueCountries(establishments = []) {
  const countries = establishments
    .map((est) => est.country_name)
    .filter(Boolean);
  return [...new Set(countries)];
}

function hasLocationData(est) {
  return Boolean(
    est.city_id
    || est.state_id
    || est.location_id
    || est.city_name
    || est.state_name
    || est.location_name
  );
}

function matchesLocation(est, locationTab) {
  if (!locationTab) return true;
  if (!hasLocationData(est)) return true;

  return (
    est.city_id === locationTab.id
    || est.state_id === locationTab.id
    || est.location_id === locationTab.id
    || est.city_name === locationTab.name
    || est.state_name === locationTab.name
    || est.location_name === locationTab.name
  );
}

function matchesCountry(est, country) {
  if (!country) return true;

  return (
    est.country_id === country.id
    || est.country_name === country.name
    || (!est.country_id && !est.country_name)
  );
}

export function filterEstablishments(establishments, { locationTab, country } = {}) {
  return establishments.filter((est) => (
    matchesCountry(est, country) && matchesLocation(est, locationTab)
  ));
}
